// Per-user, per-team snapshot of the team chat (messages + events + pins) so
// the team page can hydrate the chat panel from cache and skip the spinner
// every time the user reopens the same team. The full network fetch still
// runs in the background and overwrites with fresh data — the cache only
// covers the first frame.
//
// Keying: `coravu.teamChat:{userId}:{teamId}`. Per-user prefix so shared
// browser sign-ins don't clobber each other.

import type { PinnedMessage, TeamMessage } from "./teamMessages";
import type { TeamEvent } from "./teamEvents";

const PREFIX = "coravu.teamChat:";

// Cap the cached list lengths so localStorage doesn't accumulate megabytes
// across many teams. The real fetch loads up to 200 each; 100 is plenty to
// fill the visible chat panel on revisit.
const MAX_MESSAGES = 100;
const MAX_EVENTS = 100;

export type CachedTeamChat = {
  messages: TeamMessage[];
  events: TeamEvent[];
  pins: PinnedMessage[];
};

function key(userId: string, teamId: string): string {
  return `${PREFIX}${userId}:${teamId}`;
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

export function readCachedTeamChat(
  userId: string,
  teamId: string
): CachedTeamChat | undefined {
  const s = storage();
  if (!s) return undefined;
  try {
    const raw = s.getItem(key(userId, teamId));
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw) as CachedTeamChat | null;
    if (!parsed || typeof parsed !== "object") return undefined;
    if (
      !Array.isArray(parsed.messages) ||
      !Array.isArray(parsed.events) ||
      !Array.isArray(parsed.pins)
    ) {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

export function writeCachedTeamChat(
  userId: string,
  teamId: string,
  chat: CachedTeamChat
): void {
  const s = storage();
  if (!s) return;
  try {
    const trimmed: CachedTeamChat = {
      messages: chat.messages.slice(-MAX_MESSAGES),
      events: chat.events.slice(-MAX_EVENTS),
      pins: chat.pins,
    };
    s.setItem(key(userId, teamId), JSON.stringify(trimmed));
  } catch {
    // quota / private mode — ignore
  }
}

export function clearCachedTeamChat(userId: string, teamId: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(key(userId, teamId));
  } catch {
    // ignore
  }
}
